/**
 * / — lobby. Pixel wordmark + 6-character lineup reveal + arcade CTAs.
 * "새 토너먼트 시작" or "이어하기" when a resume snapshot exists.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logoUrl from '@/assets/brand/logo.svg?url';
import { getCharacterAsset, LINEUP_ORDER } from '@/characters/manifest';
import { CharacterAvatar } from '@/components/character/CharacterAvatar';
import { listOpenResumes } from '@/storage/resume';

export function HomeRoute() {
  const [resumableId, setResumableId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rows = await listOpenResumes();
      if (cancelled) return;
      setResumableId(rows[0]?.tournamentId ?? null);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const startFresh = () => {
    const seed = `play-${Date.now().toString(36)}`;
    navigate(`/play?seed=${encodeURIComponent(seed)}`);
  };

  const resume = () => {
    if (!resumableId) return;
    const seedMatch = resumableId.match(/^tourney-(.+)$/);
    const seed = seedMatch?.[1] ?? 'play-default';
    navigate(`/play?seed=${encodeURIComponent(seed)}`);
  };

  return (
    <main
      className="relative min-h-dvh overflow-hidden bg-bg text-fg crt-scanlines"
      data-testid="home-route"
    >
      {/* Grid overlay — subtle arcade vibe. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(hsl(var(--color-accent-alt)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--color-accent-alt)) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative z-10 flex min-h-dvh flex-col items-center gap-8 px-6 py-10 text-center">
        {/* Logo + tagline */}
        <header className="flex flex-col items-center gap-3 reveal-blur">
          <img
            src={logoUrl}
            alt="POKER ∞ AI"
            className="pixelated w-[240px] max-w-[72vw] drop-shadow-[0_0_18px_hsl(var(--color-accent-alt)/0.35)]"
            draggable={false}
          />
          <p className="font-display text-[10px] tracking-[0.28em] text-accent-alt text-glow-cyan uppercase">
            6-MAX · NO LIMIT · OFFLINE
          </p>
          <p className="font-retro text-lg text-muted max-w-xs">
            5명의 AI 에이전트와 겨루는 텍사스 홀덤 토너먼트
          </p>
        </header>

        {/* Character lineup — staggered reveal */}
        <section
          aria-label="캐릭터 라인업"
          data-testid="home-lineup"
          className="flex flex-wrap items-end justify-center gap-3 max-w-xs"
        >
          {LINEUP_ORDER.map((codename, i) => {
            const asset = getCharacterAsset(codename);
            return (
              <figure
                key={codename}
                className="reveal-up flex flex-col items-center gap-1"
                style={{ animationDelay: `${200 + i * 90}ms` }}
              >
                <CharacterAvatar codename={codename} size="md" />
                <figcaption
                  className="font-display text-[8px] tracking-widest text-fg uppercase"
                  title={asset.tagline}
                >
                  {asset.displayName}
                </figcaption>
              </figure>
            );
          })}
        </section>

        {/* CTAs */}
        <div
          className="flex w-full max-w-[320px] flex-col gap-3 reveal-up"
          style={{ animationDelay: '850ms' }}
        >
          <button
            type="button"
            onClick={startFresh}
            className="pixel-btn pixel-btn--primary"
            data-testid="start-new"
          >
            ▶ NEW GAME
          </button>
          {loaded && resumableId ? (
            <button
              type="button"
              onClick={resume}
              className="pixel-btn pixel-btn--cyan"
              data-testid="resume"
              title={resumableId}
            >
              ↻ CONTINUE
            </button>
          ) : null}
        </div>

        {/* Footer nav */}
        <nav
          className="mt-auto flex gap-6 text-[10px] font-display uppercase tracking-widest reveal-up"
          style={{ animationDelay: '1100ms' }}
        >
          <Link to="/review" className="text-accent-alt text-glow-cyan hover:underline">
            Review
          </Link>
          <span aria-hidden="true" className="text-border">
            ·
          </span>
          <Link to="/settings" className="text-accent-alt text-glow-cyan hover:underline">
            Settings
          </Link>
        </nav>
      </div>
    </main>
  );
}
